type LogLevel = 'info' | 'warn' | 'error' | 'success' | 'debug' | 'metric' | 'time';

interface LogConfig {
  emoji: string;
  color: string;
}

const LOG_LEVELS: Record<LogLevel, LogConfig> = {
  info: { emoji: '📘', color: '#2196F3' },
  warn: { emoji: '⚠️', color: '#FFC107' },
  error: { emoji: '🚨', color: '#F44336' },
  success: { emoji: '✅', color: '#4CAF50' },
  debug: { emoji: '🔍', color: '#9C27B0' },
  metric: { emoji: '📊', color: '#FF9800' },
  time: { emoji: '⏱️', color: '#607D8B' }
};

class DevLogger {
  private static instance: DevLogger;
  private isEnabled: boolean;

  private constructor() {
    this.isEnabled = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
  }

  static getInstance(): DevLogger {
    if (!DevLogger.instance) {
      DevLogger.instance = new DevLogger();
    }
    return DevLogger.instance;
  }

  private formatMessage(level: LogLevel, component: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `${LOG_LEVELS[level].emoji} [${timestamp}] [${component}] ${message}`;
  }

  private log(level: LogLevel, component: string, message: string, data?: unknown) {
    if (!this.isEnabled) return;

    const formattedMessage = this.formatMessage(level, component, message);
    const style = `color: ${LOG_LEVELS[level].color}; font-weight: bold;`;

    console.log(`%c${formattedMessage}`, style);
    if (typeof data !== 'undefined') {
      console.log('  └─ ', data);
    }
  }

  info(component: string, message: string, data?: unknown) {
    this.log('info', component, message, data);
  }

  warn(component: string, message: string, data?: unknown) {
    this.log('warn', component, message, data);
  }

  error(component: string, message: string, data?: unknown) {
    this.log('error', component, message, data);
  }

  success(component: string, message: string, data?: unknown) {
    this.log('success', component, message, data);
  }

  debug(component: string, message: string, data?: unknown) {
    this.log('debug', component, message, data);
  }

  metric(component: string, message: string, data?: unknown) {
    this.log('metric', component, message, data);
  }

  time(component: string, message: string, data?: unknown) {
    this.log('time', component, message, data);
  }

  // Group related logs
  group(component: string, groupName: string, callback: () => void) {
    if (!this.isEnabled) return;
    
    console.group(`${LOG_LEVELS.info.emoji} [${component}] ${groupName}`);
    callback();
    console.groupEnd();
  }
}

const isProd = process.env.NODE_ENV === 'production';

export const logger = DevLogger.getInstance(); 

const normalizeMetadata = (data: unknown): Record<string, unknown> | undefined => {
  if (data && typeof data === 'object') {
    return data as Record<string, unknown>;
  }
  return undefined;
};

export const prodLogger = {
  ...logger,
  prodDebug: (component: string, message: string, data?: unknown) => {
      if (isProd) {
          // Send to error monitoring service instead of console
          import('../services/errorMonitoring').then(({ errorMonitoring }) => {
            errorMonitoring.logInfo(`${component}: ${message}`, {
              component,
              level: 'debug',
              metadata: normalizeMetadata(data)
            });
          }).catch((error) => {
            // Fallback to console if error monitoring fails
            console.debug(`[${component}] ${message}`, data, '(Error monitoring unavailable:', error.message, ')');
          });
          return;
      }
      // In development, use regular debug logging
      logger.debug(component, message, data);
  },
  
  prodError: (component: string, message: string, error?: Error, data?: unknown) => {
      if (isProd) {
          // Send to error monitoring service
          import('../services/errorMonitoring').then(({ errorMonitoring }) => {
            const errorToLog = error || new Error(message);
            errorMonitoring.logError(errorToLog, {
              component,
              level: 'error',
              metadata: normalizeMetadata(data)
            });
          }).catch((importError) => {
            // Fallback to console if error monitoring fails
            console.error(`[${component}] ${message}`, error, data, '(Error monitoring unavailable:', importError.message, ')');
          });
          return;
      }
      // In development, use regular error logging
      const mergedData = normalizeMetadata(data);
      logger.error(component, message, mergedData ? { error, ...mergedData } : { error });
  },

  prodWarning: (component: string, message: string, data?: unknown) => {
      if (isProd) {
          // Send to error monitoring service
          import('../services/errorMonitoring').then(({ errorMonitoring }) => {
            errorMonitoring.logWarning(`${component}: ${message}`, {
              component,
              level: 'warning',
              metadata: normalizeMetadata(data)
            });
          }).catch((error) => {
            // Fallback to console if error monitoring fails
            console.warn(`[${component}] ${message}`, data, '(Error monitoring unavailable:', error.message, ')');
          });
          return;
      }
      // In development, use regular warning logging
      logger.warn(component, message, data);
  }
};

// CHEAT SHEET

/*

Different log types and when to use them:
logger.info('ComponentName', 'Starting process');  // 📘 General information
logger.warn('ComponentName', 'Potential issue');   // ⚠️ Warnings/potential problems
logger.error('ComponentName', 'Failed to load');   // 🚨 Errors/exceptions
logger.success('ComponentName', 'Task complete');  // ✅ Successful operations
logger.debug('ComponentName', 'State update');     // 🔍 Debugging information
logger.metric('ComponentName', 'Performance');     // 📊 Metrics/measurements
logger.time('ComponentName', 'Operation timing');  // ⏱️ Timing information

With data objects:
logger.info('ComponentName', 'User state', {
  isLoggedIn: true,
  lastActive: new Date()
});

Grouping related logs:
logger.group('ComponentName', 'State Update', () => {
  logger.info('ComponentName', 'Current state', currentState);
  logger.metric('ComponentName', 'Performance', metrics);
  logger.debug('ComponentName', 'Debug data', debugInfo);
});

*/